'use client'

import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_COLLECTION,
  DELETE_COLLECTION,
  GET_COLLECTION,
  UPDATE_COLLECTION,
} from '../collectionsQueries'
import { DELETE_MANY_CONTENT_BLOCKS } from '../../ContentBlocks/contentBlockQueries'
import { CollectionFormValues } from '../types'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import ProductSelect from '@/components/shared/inputComponents/ProductSelect'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'
import { buildCollectionPublicUrl } from '../collectionPublicUrl'
import { ContentBlockSelector } from '@/containers/ContentBlocks/components/ContentBlockSelector'
import { DraggableContentBlockList } from '@/containers/ContentBlocks/components/DraggableContentBlockList'
import InlineContentBlockForm from '@/containers/ContentBlocks/components/InlineContentBlockForm'
import { RemoveBlockDialog } from '@/containers/ContentBlocks/components/RemoveBlockDialog'
import { ContentBlockItem } from '@/containers/ContentBlocks/components/DraggableContentBlockItem'

const formSchema = z.object({
  collection_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
        meta_information: z
          .object({
            id: z.number().optional(),
            permalink: z.string().optional(),
            meta_title: z.string().optional(),
            meta_description: z.string().optional(),
          })
          .optional(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  images: z.array(z.string()).optional(),
  selected_products: z.array(z.object({ id: z.number() })),
  content_blocks: z.array(z.object({ id: z.number() })).optional(),
})

interface CollectionFormProps {
  data?: {
    id: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function CollectionForm({
  data,
  onDirtyChange,
  onRequestClose,
}: CollectionFormProps) {
  const { allLanguages, languageId, language } = useLanguage()
  const [id, setId] = useState(data?.id)
  const [contentBlocks, setContentBlocks] = useState<ContentBlockItem[]>([])
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [inlineFormOpen, setInlineFormOpen] = useState(false)
  const [editingBlockId, setEditingBlockId] = useState<number | null>(null)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [blockToRemove, setBlockToRemove] = useState<ContentBlockItem | null>(null)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueCollection: collectionData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_COLLECTION, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  // While an EXISTING collection's data is still in flight, suppress the
  // link entirely rather than flashing a misleading "no permalink yet" —
  // mirrors PageDetails' `if (!pageData) return null` gating. A brand-new,
  // unsaved collection has no `id`/fetch at all, so it still shows the
  // "save first" reason state immediately.
  const publicUrlInfo = useMemo(() => {
    if (id && loadingData) return null
    return buildCollectionPublicUrl({ collection: collectionData, language })
  }, [id, loadingData, collectionData, language])

  const form = useForm<CollectionFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      collection_profiles: [],
      images: [],
      selected_products: [],
      content_blocks: [],
    },
  })

  const [updateCollection, { loading: updateLoading }] = useMutation(
    UPDATE_COLLECTION,
    {
      onError: e => {
        toast.error('Failed to update collection')
      },
      onCompleted: () => {
        toast.success('Collection updated successfully')
        form.reset()
      },
      refetchQueries: ['GET_COLLECTIONS'],
    }
  )

  const [createCollection, { loading: createLoading }] = useMutation(
    CREATE_COLLECTION,
    {
      onError: e => {
        toast.error('Failed to create collection')
      },
      onCompleted: response => {
        toast.success('Collection created successfully')
        const newId = response.createNewCollection.id
        setId(newId)
        form.reset()
      },
      refetchQueries: ['GET_COLLECTIONS'],
    }
  )

  const [deleteManyContentBlocks] = useMutation(DELETE_MANY_CONTENT_BLOCKS)

  const [deleteCollection] = useMutation(DELETE_COLLECTION, {
    refetchQueries: ['GET_COLLECTIONS'],
  })

  const handleDelete = () => {
    if (id) {
      const productCount = collectionData?.product_containers?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteCollection({
              variables: { where: { id } },
            })
            toast.success('Collection deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete collection')
          }
        },
        description: productCount
          ? `This collection contains ${productCount} product(s). This action cannot be undone.`
          : 'Are you sure you want to delete this collection? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  const handleAddBlock = (block: ContentBlockItem) => {
    setContentBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
    setSelectorOpen(false)
  }

  const handleEditBlock = (blockId: number) => {
    setEditingBlockId(blockId)
    setInlineFormOpen(true)
  }

  const handleCreateNew = () => {
    setEditingBlockId(null)
    setInlineFormOpen(true)
  }

  const handleInlineCreated = (block: ContentBlockItem) => {
    setContentBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
  }

  const handleInlineUpdated = (block: ContentBlockItem) => {
    setContentBlocks(prev =>
      prev.map(b => (b.id === block.id ? { ...b, ...block, arrangement: b.arrangement } : b))
    )
  }

  const handleReorder = (reorderedBlocks: ContentBlockItem[]) => {
    setContentBlocks(reorderedBlocks)
  }

  const handleRemove = (blockId: number) => {
    const block = contentBlocks.find(b => b.id === blockId)
    if (block) {
      setBlockToRemove(block)
      setRemoveDialogOpen(true)
    }
  }

  const handleRemoveFromCollection = () => {
    if (blockToRemove) {
      setContentBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
      setBlockToRemove(null)
      setRemoveDialogOpen(false)
    }
  }

  const handleDeletePermanently = async () => {
    if (blockToRemove) {
      try {
        await deleteManyContentBlocks({ variables: { ids: [blockToRemove.id] } })
        setContentBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
        toast.success('Content block deleted permanently')
        setRemoveDialogOpen(false)
      } catch {
        toast.error('Failed to delete content block')
      }
      setBlockToRemove(null)
    }
  }

  const getBlockName = (block: ContentBlockItem) => {
    const profile = block.content_block_profiles.find(p => p.language === 'en')
    return profile?.name || block.content_block_profiles[0]?.name || `Block #${block.id}`
  }

  useEffect(() => {
    if (!loadingData) {
      if (collectionData) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          collectionData.collection_profiles?.filter(
            (p: any) => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: any) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
              meta_information: {
                id: existingProfile.meta_information?.id,
                permalink: existingProfile.meta_information?.permalink ?? '',
                meta_title: existingProfile.meta_information?.meta_title ?? '',
                meta_description:
                  existingProfile.meta_information?.meta_description ?? '',
              },
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
              meta_information: {
                permalink: '',
                meta_title: '',
                meta_description: '',
              },
            }
          }
        })

        form.reset({
          collection_profiles: allProfiles,
          images: collectionData.main_image
            ? [collectionData.main_image.src]
            : [],
          selected_products: collectionData.product_containers || [],
          content_blocks: (collectionData.content_blocks ?? []).map((b: { id: number }) => ({ id: b.id })),
        })

        // Initialize content blocks from collection data
        if (collectionData.content_blocks) {
          setContentBlocks(
            collectionData.content_blocks.map((block: ContentBlockItem, index: number) => ({
              id: block.id,
              type: block.type,
              style: block.style || null,
              arrangement: block.arrangement ?? index,
              content_block_profiles: block.content_block_profiles || [],
              main_image: block.main_image || null,
              gallery_images: block.gallery_images || [],
              video_type: block.video_type || null,
              video_link: block.video_link || null,
              text_color: block.text_color || null,
              background_color: block.background_color || null,
              config: block.config ?? null,
              product_containers: block.product_containers || [],
              grid_product_containers: block.grid_product_containers || [],
              categories: block.categories || [],
            }))
          )
        } else {
          setContentBlocks([])
        }
      } else {
        // For NEW forms, initialize with profiles for all languages
        form.reset({
          collection_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
            meta_information: {
              permalink: '',
              meta_title: '',
              meta_description: '',
            },
          })),
          images: [],
          selected_products: [],
          content_blocks: [],
        })
        setContentBlocks([])
      }
    }
  }, [collectionData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  function onSubmit(values: CollectionFormValues) {
    if (id) {
      updateCollection({
        variables: {
          where: { id },
          collection_profiles: values.collection_profiles.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          images: values.images,
          selected_products: values.selected_products,
          content_blocks: contentBlocks.map(b => ({ id: b.id })),
        },
      })
    } else {
      createCollection({
        variables: {
          collection_profiles: values.collection_profiles.map(p => ({
            name: p.name,
            description: p.description,
            language: p.language,
            meta_information: p.meta_information,
          })),
          images: values.images,
          selected_products: values.selected_products,
          content_blocks: contentBlocks.map(b => ({ id: b.id })),
        },
      })
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Collection</SheetTitle>
          <LanguageSwitcher />
        </div>
        {id && <SheetDescription>ID: {id}</SheetDescription>}
        {publicUrlInfo && (
          <div className="flex items-center gap-2 text-sm">
            {publicUrlInfo.url === null ? (
              <span className="text-muted-foreground">{publicUrlInfo.reason}</span>
            ) : publicUrlInfo.clickable ? (
              <a
                href={publicUrlInfo.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
              >
                {publicUrlInfo.url}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            ) : (
              <>
                <span className="font-mono text-muted-foreground">
                  {publicUrlInfo.url}
                </span>
                <span className="text-muted-foreground">
                  ({publicUrlInfo.reason})
                </span>
              </>
            )}
          </div>
        )}
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Name"
                description="This is your public display name."
                control={form.control}
                name={`collection_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`collection_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Title"
                control={form.control}
                name={`collection_profiles.${languageId}.meta_information.meta_title`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Description"
                control={form.control}
                name={`collection_profiles.${languageId}.meta_information.meta_description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Meta Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Permalink"
                control={form.control}
                name={`collection_profiles.${languageId}.meta_information.permalink`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Image"
                description="Upload collection image"
                control={form.control}
                name="images"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Products"
                description="Select products for this collection"
                control={form.control}
                name="selected_products"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ProductSelect field={field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="border-t pt-4 mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Content Blocks</h3>
                  <Button type="button" onClick={() => setSelectorOpen(true)}>
                    Add Block
                  </Button>
                </div>

                {contentBlocks.length > 0 ? (
                  <DraggableContentBlockList
                    blocks={contentBlocks}
                    onReorder={handleReorder}
                    onRemove={handleRemove}
                    onEdit={handleEditBlock}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">No content blocks assigned to this collection.</p>
                )}

                <ContentBlockSelector
                  open={selectorOpen}
                  onOpenChange={setSelectorOpen}
                  assignedBlockIds={contentBlocks.map(b => b.id)}
                  onSelect={handleAddBlock}
                  onCreateNew={handleCreateNew}
                />

                <InlineContentBlockForm
                  open={inlineFormOpen}
                  onOpenChange={setInlineFormOpen}
                  blockId={editingBlockId}
                  onCreated={handleInlineCreated}
                  onUpdated={handleInlineUpdated}
                />

                <RemoveBlockDialog
                  open={removeDialogOpen}
                  onOpenChange={setRemoveDialogOpen}
                  blockName={blockToRemove ? getBlockName(blockToRemove) : ''}
                  onRemoveFromPage={handleRemoveFromCollection}
                  onDeletePermanently={handleDeletePermanently}
                />
              </div>

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
